import React, { Fragment, useCallback, useMemo } from 'react';
import { HashLink as Link } from 'react-router-hash-link';
import classNames from 'classnames/bind';
import { v4 } from 'uuid';

import FocusedImage from 'Components/FocusedImage';
import Icon from 'Components/Icon';
import { isExternalLink, isValidTarget, stripTags } from 'Utils';
import { injectUrlVars } from 'Utils/Validators';
import { gaPush } from 'Utils/ga_push';
import AssetModel from 'Stores/AssetModel';
import styles from './styles.scss';

const cx = classNames.bind(styles);

const Typography = ({ type, className, children: __html }) => {
  switch (type) {
    case 'h1':
      return <h1 className={className} dangerouslySetInnerHTML={{ __html }} />;
    case 'h3':
      return <h3 className={className} dangerouslySetInnerHTML={{ __html }} />;
    default:
      return <h2 className={className} dangerouslySetInnerHTML={{ __html }} />;
  }
};

const Cta = ({ href, trackingLabel, children, target, cnt, action }) => {
  const content = <span className={styles.linkContent}>{children}</span>;
  const track = useCallback(() => {
    gaPush({
      event: 'cta_interaction',
      category: 'CTA',
      click_type: `Bentobox ${action}${cnt + 1}`,
      click_value: trackingLabel,
    });
  }, []);

  if (!href) {
    return content;
  }

  if (isExternalLink(href)) {
    return (
      <a
        onClick={track}
        href={injectUrlVars(href)}
        target={isValidTarget(target) ? target : '_blank'}
        rel="noopener noreferrer"
        data-tracking-label={trackingLabel}
      >
        {content}
      </a>
    );
  }

  // default target is _self aka same page so we only set a target if explicitly set + valid
  return (
    <Link
      onClick={track}
      to={injectUrlVars(href)}
      target={isValidTarget(target) ? target : undefined}
      data-tracking-label={trackingLabel}
    >
      {content}
    </Link>
  );
};

const Tile = ({
  className,
  headline,
  headlineLevel,
  image,
  imageMobile,
  forceUppercase,
  cta,
  consumptionText,
}) => (
  <div className={cx(styles.tile, styles.image, styles[className])}>
    <Cta {...cta} action="tile">
      <FocusedImage
        image={new AssetModel(image)}
        imageMobile={new AssetModel(imageMobile ?? image)}
      />
      <Typography
        type={headlineLevel}
        className={cx(styles.title, forceUppercase && styles.forceUppercase)}
      >
        {headline}
      </Typography>
      {consumptionText && (
        <div
          className="sr-only"
          dangerouslySetInnerHTML={{ __html: `<p>${stripTags(consumptionText)}</p>` }}
        />
      )}
    </Cta>
  </div>
);

const Legal = ({ className, children: __html }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html }} aria-hidden="true" />
);

const NextBestAction = ({ icon, label: __html, cta, cnt }) => (
  <li className={cx(styles.nba)}>
    <Cta {...cta} cnt={cnt} action="nba">
      {icon && <Icon className={styles.icon} name={`bentobox-${icon}`} />}
      <label dangerouslySetInnerHTML={{ __html }} />
    </Cta>
  </li>
);

const NextBestActions = ({ nbas }) => (
  <ul className={cx(styles.nbas)}>
    {nbas.map((nba, cnt) => (
      <NextBestAction {...nba} cnt={cnt} key={`nba-${cnt}`} />
    ))}
  </ul>
);

const StickyBox = ({ tiles }) => {
  const [stickyTiles, isStickyBoxEmpty] = useMemo(() => {
    // first tile is not sticky
    // only render sticky box in case one of the two remaining tiles have consumptionText
    const stickyTiles = [...tiles]
      .map((all, cnt) => ({ ...all, cnt }))
      .slice(1)
      .filter((tile) => tile.consumptionText);
    const empty = stickyTiles.length === 0;
    return [stickyTiles, empty];
  }, [tiles]);

  if (isStickyBoxEmpty) {
    return null;
  }

  return (
    <div className={styles.stickyBox}>
      <div className={styles.stickyBoxInner}>
        {stickyTiles.map(({ consumptionText, cnt }) => (
          <Legal
            key={`bentobox-vag-${cnt}-${v4()}`}
            className={cx(styles.text, styles[`legal${cnt}`])}
          >
            {stripTags(consumptionText)}
          </Legal>
        ))}
      </div>
    </div>
  );
};

const getAdditionalGridClass = (a, b, c) => {
  switch (true) {
    case !a && !b && !c:
      return styles.noLegals;
    case !a && (b || c):
      return styles.noFirst;
    case a && !b && !c:
      return styles.noLast;
    default:
      return styles.allLegals;
  }
};

const Bentobox = ({ tiles: tilesRaw, nbas: nbasRaw }) => {
  // TODO: Find error why cta is passed as empty
  // initialization in case no cta data is delivered
  const tiles = useMemo(
    () =>
      tilesRaw.map((tile, cnt) => {
        tile.cta = tile.cta ?? {
          href: '/',
          trackingLabel: `initialization-for-tile-${cnt + 1}`,
        };
        return tile;
      }),
    [tilesRaw]
  );

  // initialization in case no cta data is delivered
  const nbas = useMemo(
    () =>
      nbasRaw.map((nba, cnt) => {
        nba.cta = nba.cta ?? {
          href: '/',
          trackingLabel: `initialization-for-nba-${cnt + 1}`,
        };
        return nba;
      }),
    [nbasRaw]
  );

  return (
    <section
      className={cx(
        styles.bentoBox,
        getAdditionalGridClass(
          !!tiles[0].consumptionText,
          !!tiles[1].consumptionText,
          !!tiles[2].consumptionText
        )
      )}
    >
      {tiles.map(
        (
          { image, imageMobile, headline, headlineLevel, forceUppercase, consumptionText, cta },
          cnt
        ) => (
          <Fragment key={`bentobox-key-${cnt}-${v4()}`}>
            <Tile
              className={`tile${cnt}`}
              image={image}
              imageMobile={imageMobile ?? image}
              headline={headline}
              headlineLevel={headlineLevel}
              forceUppercase={forceUppercase}
              cta={{ ...cta, cnt }}
              consumptionText={consumptionText}
            />
            {consumptionText && (
              <Legal
                className={cx(
                  styles.text,
                  styles[`legal${cnt}`],
                  cnt > 0 && styles.onlyVerticalNba
                )}
              >
                {stripTags(consumptionText)}
              </Legal>
            )}
          </Fragment>
        )
      )}
      <NextBestActions nbas={nbas} />
      <StickyBox tiles={tiles} />
    </section>
  );
};

export default Bentobox;
